/**
 * Session state for the console. On load we ask `GET /api/user`; a 401 just
 * means "show the login screen". Login/logout go through {@link api}.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.js';
import { effectivePermissions, hasPermission } from './permissions.js';
import type { CurrentUser, Permission } from './types.js';

interface AuthValue {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  /** Sign in with a Google ID token (from Google Identity Services). */
  loginWithGoogle: (idToken: string, remember?: boolean) => Promise<void>;
  /** Create an account (self-service sign-up) and sign in to it. */
  register: (body: Parameters<typeof api.register>[0]) => Promise<void>;
  logout: () => Promise<void>;
  /** Whether the signed-in user holds a permission slug (ADR-0010). */
  can: (permission: Permission) => boolean;
  /**
   * Re-run the `GET /api/user` bootstrap. Used by the SSO callback landing
   * page (ADR-0017) — the session cookie was set by a plain browser
   * navigation the SPA never saw, so it has to ask again.
   */
  refresh: () => Promise<void>;
  /** Operator only: sign in as a customer's user, then reload into their organisation. */
  impersonate: (userId: string) => Promise<void>;
  /** Hand the session back to the operator, then reload. */
  stopImpersonating: () => Promise<void>;
}

// Branding, capabilities and every screen's data belong to the organisation the
// session is in, so switching user reloads the console rather than patching state.
function reloadConsole(): void {
  window.location.assign(import.meta.env.BASE_URL);
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async (): Promise<void> => {
    try {
      setUser(await api.currentUser());
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let active = true;
    bootstrap().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [bootstrap]);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    setUser(await api.login(email, password, remember));
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string, remember = false) => {
    setUser(await api.loginWithGoogle(idToken, remember));
  }, []);

  const register = useCallback(async (body: Parameters<typeof api.register>[0]) => {
    setUser(await api.register(body));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const impersonate = useCallback(async (userId: string) => {
    await api.startImpersonation(userId);
    reloadConsole();
  }, []);

  const stopImpersonating = useCallback(async () => {
    await api.stopImpersonation();
    reloadConsole();
  }, []);

  const permissions = useMemo(
    () => effectivePermissions(user?.permissions ?? [], user?.restrictedView === true),
    [user],
  );
  const can = useCallback((permission: Permission): boolean => hasPermission(permissions, permission), [permissions]);

  const value = useMemo<AuthValue>(
    () => ({ user, loading, login, loginWithGoogle, register, logout, can, refresh: bootstrap, impersonate, stopImpersonating }),
    [user, loading, login, loginWithGoogle, register, logout, can, bootstrap, impersonate, stopImpersonating],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
